import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { MON_NAME, dim, fmtD, invoiceRows, money, nowYM, num2, today0, ymAdd, ymd, type Data, type Deadline } from '@biuro/shared';
import { monthLabel } from './close.ts';
import { isRedDay, shortLabel } from './ui.tsx';

// ---------- kafel z liczbą ----------
export function Kpi({ label, icon, value, unit, sub, subTone, focus, onClick, children }: {
  label: string; icon: ReactNode; value: ReactNode; unit?: string; sub?: ReactNode; subTone?: 'bad' | 'warn' | '';
  focus?: boolean; onClick?: () => void; children?: ReactNode;
}) {
  return (
    <button type="button" className={`kpi ${focus ? 'focus' : ''}`} onClick={onClick}>
      <span className="k-top"><span>{label}</span>{icon}</span>
      <span className="k-val">{value}{unit && <small>{unit}</small>}</span>
      {children}
      <span className={`k-sub ${subTone || ''}`}>{sub}</span>
    </button>
  );
}

// ---------- wykres kosztów usług zagranicznych ----------
const MON_SHORT = ['sty', 'lut', 'mar', 'kwi', 'maj', 'cze', 'lip', 'sie', 'wrz', 'paź', 'lis', 'gru'];
const SERIES = ['var(--s1)', 'var(--s2)', 'var(--s3)', 'var(--s4)', 'var(--s5)'];
const OTHER = 'var(--s0)';
/** Klucz faktur bez usługi („Inne”) – nie koliduje z identyfikatorami usług. */
const OTHER_KEY = '\u0000inne';

function niceMax(v: number): number {
  if (v <= 0) return 100;
  const p = Math.pow(10, Math.floor(Math.log10(v)));
  for (const m of [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10]) if (m * p >= v) return m * p;
  return 10 * p;
}
const compact = (v: number) => (v >= 1000 ? `${(v / 1000).toLocaleString('pl-PL', { maximumFractionDigits: 1 })} tys.` : String(Math.round(v)));

/** Szerokość elementu w pikselach – wykres rysuje się w skali 1:1, więc napisy mają zawsze ten sam rozmiar. */
function useWidth(): [(el: HTMLDivElement | null) => void, number] {
  const [el, setEl] = useState<HTMLDivElement | null>(null);
  const [w, setW] = useState(720);
  useEffect(() => {
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([e]) => setW(Math.max(280, Math.round(e.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, [el]);
  return [setEl, w];
}

export function CostChart({ data }: { data: Data }) {
  const [hover, setHover] = useState<number | null>(null);
  const [box, width] = useWidth();
  const cur = nowYM();
  const months = useMemo(() => Array.from({ length: 12 }, (_, i) => ymAdd(cur, i - 11)), [cur]);
  const { series, matrix, totals, grand } = useMemo(() => {
    const byKey = new Map<string, number[]>();
    for (const x of invoiceRows(data)) {
      if (x.c.draft || x.c.net == null) continue;
      const i = months.indexOf(x.c.month);
      if (i < 0) continue;
      // Faktury „Inne” (bez usługi) idą do „Pozostałe”, nie jako osobna seria.
      const k = x.inv.serviceId || OTHER_KEY;
      const arr = byKey.get(k) || Array(12).fill(0);
      arr[i] += x.c.net;
      byKey.set(k, arr);
    }
    const ranked = [...byKey.entries()].map(([k, arr]) => ({ k, arr, sum: arr.reduce((a, b) => a + b, 0) })).sort((a, b) => b.sum - a.sum);
    const named = ranked.filter((r) => r.k !== OTHER_KEY);
    const top = named.slice(0, 5);
    const rest = [...named.slice(5), ...ranked.filter((r) => r.k === OTHER_KEY)];
    const series = top.map((r, i) => ({ key: r.k, name: data.services[r.k]?.name || 'Usługa usunięta', color: SERIES[i], arr: r.arr, sum: r.sum }));
    if (rest.length) {
      const arr = Array(12).fill(0);
      for (const r of rest) r.arr.forEach((v, i) => (arr[i] += v));
      series.push({ key: 'other', name: 'Pozostałe', color: OTHER, arr, sum: arr.reduce((a, b) => a + b, 0) });
    }
    const totals = months.map((_, i) => series.reduce((a, s) => a + s.arr[i], 0));
    return { series, matrix: series.map((s) => s.arr), totals, grand: totals.reduce((a, b) => a + b, 0) };
  }, [data, months]);

  const W = width, H = width < 500 ? 190 : 230, L = 48, R = 4, T = 10, B = 26;
  const iw = W - L - R, ih = H - T - B;
  const max = niceMax(Math.max(...totals));
  const band = iw / 12;
  const bw = Math.min(24, band * 0.56);
  const y = (v: number) => T + ih - (v / max) * ih;
  const ticks = [0, max / 4, max / 2, (3 * max) / 4, max];
  const avg = grand / 12;

  return (
    <>
      <div className="card-head">
        <div>
          <h2>Koszty usług zagranicznych</h2>
          <p>Netto w złotych, ostatnie 12 miesięcy</p>
        </div>
        <div className="tot">
          <div className="big-total">{money(grand)}</div>
          <div className="muted small">średnio {money(avg)} miesięcznie</div>
        </div>
      </div>
      {grand === 0 ? (
        <div className="empty">Brak faktur z ostatnich 12 miesięcy. Wykres pojawi się po dodaniu pierwszych faktur.</div>
      ) : (
        <div className="chart" ref={box} onMouseLeave={() => setHover(null)}>
          <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img" aria-label={`Koszty usług zagranicznych w ostatnich 12 miesiącach, razem ${money(grand)}`}>
            <g className="grid">
              {ticks.map((t) => <line key={t} x1={L} x2={W - R} y1={y(t)} y2={y(t)} />)}
            </g>
            {ticks.map((t) => <text key={t} className="tick" x={L - 8} y={y(t) + 4} textAnchor="end">{compact(t)}</text>)}
            {months.map((m, i) => {
              const cx = L + band * i + band / 2;
              let acc = 0;
              const segs = matrix.map((arr, si) => ({ v: arr[i], si })).filter((s) => s.v > 0);
              return (
                <g key={m} className={`col ${hover === i ? 'on' : ''}`} tabIndex={0} aria-label={`${monthLabel(m)}: ${money(totals[i])}`}
                  onMouseEnter={() => setHover(i)} onFocus={() => setHover(i)} onBlur={() => setHover(null)} onClick={() => setHover(hover === i ? null : i)}>
                  <rect className="hit" x={L + band * i + 2} y={T} width={band - 4} height={ih} rx={6} />
                  {segs.map((s, k) => {
                    const y0 = y(acc), y1 = y(acc + s.v);
                    acc += s.v;
                    const top = k === segs.length - 1;
                    const h = Math.max(0, y0 - y1 - (k > 0 ? 2 : 0));
                    const yy = y1;
                    if (h <= 0) return null;
                    if (!top) return <rect key={k} x={cx - bw / 2} y={yy} width={bw} height={h} fill={series[s.si].color} />;
                    const r = Math.min(4, h, bw / 2);
                    const x0 = cx - bw / 2, x1 = cx + bw / 2, yb = yy + h;
                    return <path key={k} fill={series[s.si].color} d={`M${x0},${yb}V${yy + r}Q${x0},${yy} ${x0 + r},${yy}H${x1 - r}Q${x1},${yy} ${x1},${yy + r}V${yb}Z`} />;
                  })}
                  <text className={`xl ${m === cur ? 'cur' : ''}`} x={cx} y={H - 6} textAnchor="middle">{MON_SHORT[+m.slice(5) - 1]}</text>
                </g>
              );
            })}
          </svg>
          {hover != null && (
            <div className="tip" style={{ left: Math.min(Math.max(0, L + band * hover + band / 2 - 95), W - 200), top: 0 }}>
              <b>{monthLabel(months[hover])}</b>
              {series.filter((s) => s.arr[hover] > 0).map((s) => (
                <div className="r" key={s.key}><span><i style={{ background: s.color }} />{s.name}</span><span>{money(s.arr[hover])}</span></div>
              ))}
              {totals[hover] === 0 && <div className="muted">Brak faktur</div>}
              <div className="r sum"><span>Razem</span><span>{money(totals[hover])}</span></div>
            </div>
          )}
          <div className="legend">
            {series.map((s) => <span key={s.key}><i style={{ background: s.color }} />{s.name}</span>)}
          </div>
          <div className="sr-only"><table>
            <caption>Koszty usług zagranicznych netto w złotych</caption>
            <thead><tr><th>Miesiąc</th>{series.map((s) => <th key={s.key}>{s.name}</th>)}<th>Razem</th></tr></thead>
            <tbody>{months.map((m, i) => <tr key={m}><td>{monthLabel(m)}</td>{series.map((s) => <td key={s.key}>{num2(s.arr[i])}</td>)}<td>{num2(totals[i])}</td></tr>)}</tbody>
          </table></div>
        </div>
      )}
    </>
  );
}

// ---------- kalendarz miesiąca ----------
const DOW_HEAD = ['pon.', 'wt.', 'śr.', 'czw.', 'pt.', 'sob.', 'niedz.'];

export function MonthCalendar({ ym, list, isDone, nav }: { ym: string; list: Deadline[]; isDone: (d: Deadline) => boolean; nav?: ReactNode }) {
  const y = +ym.slice(0, 4), m = +ym.slice(5);
  const first = new Date(y, m - 1, 1);
  const lead = (first.getDay() + 6) % 7; // poniedziałek = 0
  const days = dim(y, m);
  const cells = Math.ceil((lead + days) / 7) * 7;
  const t = today0();
  const byDay = new Map<string, Deadline[]>();
  for (const d of list) byDay.set(ymd(d.due), [...(byDay.get(ymd(d.due)) || []), d]);
  return (
    <>
      <div className="card-head">
        <div><h2>{monthLabel(ym)}</h2><p>Terminy w kalendarzu miesiąca</p></div>
        {nav}
      </div>
      <div className="cal" role="grid" aria-label={`Kalendarz: ${MON_NAME[m - 1]} ${y}`}>
        {DOW_HEAD.map((d, i) => <div key={d} className={`dow ${i === 6 ? 'red' : ''}`} role="columnheader">{d}</div>)}
        {Array.from({ length: cells }, (_, i) => {
          const date = new Date(y, m - 1, i - lead + 1);
          const inMonth = date.getMonth() === m - 1;
          const key = ymd(date);
          const evs = inMonth ? byDay.get(key) || [] : [];
          const cls = ['day', inMonth ? '' : 'out', isRedDay(date) ? 'red' : '', key === ymd(t) ? 'today' : '', date < t ? 'past' : ''].join(' ');
          return (
            <div key={i} className={cls} role="gridcell" aria-label={`${fmtD(date)}${evs.length ? ': ' + evs.map((d) => d.title).join(', ') : ''}`}>
              <span className="dn">{date.getDate()}</span>
              {evs.length > 0 && (
                <span className="evs">
                  {evs.map((d) => {
                    const st = isDone(d) ? 'done' : d.due < t ? 'late' : '';
                    return <span key={d.id} className={`ev ${st}`} title={`${d.title} (${d.form})`}>{shortLabel(d)}</span>;
                  })}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
